import React, {useEffect, useMemo, useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import DsIcon from "../_UI/dsIcon";
import {formatNumber} from "../cabinetShell/config";
import {choiceEvent} from "../../store/cabinet/action";

import chevronIcon from '../../theme/assets/redesign/chevron.svg';
import chevronSmall from '../../theme/assets/redesign/m_chevron.svg';
import up1 from '../../theme/assets/redesign/events/up1.webp';
import up2 from '../../theme/assets/redesign/events/up2.webp';
import up3 from '../../theme/assets/redesign/events/up3.webp';
import past1 from '../../theme/assets/redesign/events/past1.webp';
import past2 from '../../theme/assets/redesign/events/past2.webp';
import past3 from '../../theme/assets/redesign/events/past3.webp';

// Картинки-заглушки, если у события нет своей
const UPCOMING_FALLBACK = [up1, up2, up3];
const PAST_FALLBACK = [past1, past2, past3];

// «Скоро старт» — событие начнётся в ближайшие 3 дня
const SOON_MS = 3 * 24 * 60 * 60 * 1000;

const pick = (obj, keys) => {
    for (const k of keys) {
        if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== '') return obj[k];
    }
    return undefined;
};

const toDate = (v) => {
    if (!v) return null;
    const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(String(v).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};

const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

// Ответ api/v1/events может прийти массивом или объектом-обёрткой
const extractList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    for (const key of ['data', 'events', 'items', 'list']) {
        if (Array.isArray(raw[key])) return raw[key];
    }
    const merged = [];
    ['live', 'active', 'upcoming', 'past', 'finished'].forEach(key => {
        if (Array.isArray(raw[key])) raw[key].forEach(e => merged.push({...e, __group: key}));
    });
    return merged;
};

const normalize = (e, index, now, selectedId) => {
    const id = pick(e, ['id', 'event_id', 'uuid']) ?? index;
    const start = toDate(pick(e, ['start_at', 'starts_at', 'start_date', 'date_start', 'started_at', 'start']));
    const end = toDate(pick(e, ['end_at', 'ends_at', 'end_date', 'date_end', 'finished_at', 'end']));
    const rawStatus = String(pick(e, ['status', 'state']) ?? e.__group ?? '').toLowerCase();

    let status;
    if (['live', 'active', 'started', 'running', 'in_progress'].includes(rawStatus)) status = 'live';
    else if (['past', 'finished', 'completed', 'ended', 'closed', 'done'].includes(rawStatus)) status = 'past';
    else if (['upcoming', 'soon', 'planned', 'scheduled', 'new'].includes(rawStatus)) status = 'upcoming';
    else if (end && end.getTime() < now) status = 'past';
    else if (start && start.getTime() <= now) status = 'live';
    else status = 'upcoming';

    const soon = status === 'upcoming' && start && start.getTime() - now <= SOON_MS;
    const joined = toBool(pick(e, ['joined', 'is_joined', 'is_participant', 'participating', 'selected', 'is_selected']))
        || (selectedId !== undefined && selectedId !== null && String(selectedId) === String(id));

    return {
        id,
        title: pick(e, ['title', 'name']) ?? '—',
        description: pick(e, ['short_description', 'description', 'text', 'subtitle']) ?? '',
        image: pick(e, ['image', 'picture', 'image_url', 'banner', 'cover', 'img', 'preview']),
        start,
        end,
        status,
        soon,
        participants: pick(e, ['participants_count', 'participants', 'members_count', 'members', 'users_count']),
        link: pick(e, ['link', 'url', 'details_url']),
        resultsLink: pick(e, ['results_url', 'results_link', 'link', 'url']),
        joined,
    };
};

const pad = (n) => String(n).padStart(2, '0');

const formatCountdown = (target, now) => {
    if (!target) return '—';
    let diff = Math.max(0, target.getTime() - now);
    const d = Math.floor(diff / 86400000); diff -= d * 86400000;
    const h = Math.floor(diff / 3600000); diff -= h * 3600000;
    const m = Math.floor(diff / 60000);
    return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m` : `${pad(h)}h ${pad(m)}m`;
};

const formatDate = (d) => d ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : '—';

const formatCount = (v) => {
    if (v === undefined) return '—';
    if (Array.isArray(v)) return formatNumber(v.length, 0);
    return formatNumber(v, 0);
};

const openLink = (url) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
};


// Карточка предстоящего / текущего события
const EventCard = ({event, fallback, now, onJoin, joining}) => {
    const {t} = useTranslation();
    const accent = event.status === 'live' || event.soon;
    const tone = accent ? 'red' : 'blue';
    const tagText = event.status === 'live' ? t('Идёт сейчас') : event.soon ? t('Скоро старт') : t('Предстоит');
    const timeLabel = event.status === 'live' ? t('До конца') : t('Старт через');
    const timeValue = event.status === 'live'
        ? (event.end ? formatCountdown(event.end, now) : '—')
        : formatCountdown(event.start, now);

    let cta;
    if (event.link) {
        cta = {label: t('Посмотреть детали'), onClick: () => openLink(event.link)};
    } else if (event.joined) {
        cta = {label: t('Вы участвуете'), disabled: true};
    } else {
        cta = {label: t('Участвовать'), onClick: () => onJoin(event.id), disabled: joining};
    }

    return (
        <div className={`${Styles.card} ${accent ? Styles.cardAccent : ''}`}>
            <div className={Styles.cardTop}>
                <div className={Styles.cardImage}>
                    <img src={event.image || fallback} alt=""/>
                </div>
                <div className={Styles.cardInfo}>
                    <div className={Styles.cardTitleRow}>
                        <div className={Styles.cardTitle}>{event.title}</div>
                        {event.link && <img className={Styles.cardChevron} src={chevronIcon} alt=""/>}
                    </div>
                    {event.description && <p className={Styles.cardText}>{event.description}</p>}
                    <div className={Styles.cardMeta}>
                        <div className={Styles.metaItem}>
                            <DsIcon name="calendar" className={`${Styles.metaIcon} ${Styles[tone]}`}/>
                            <div className={Styles.metaText}>
                                <span className={Styles.metaLabel}>{timeLabel}</span>
                                <span className={Styles.metaValue}>{timeValue}</span>
                            </div>
                        </div>
                        <div className={Styles.metaItem}>
                            <DsIcon name="users" className={Styles.metaIcon}/>
                            <div className={Styles.metaText}>
                                <span className={Styles.metaLabel}>{t('Участники')}</span>
                                <span className={Styles.metaValue}>{formatCount(event.participants)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${Styles.tag} ${Styles[`tag_${tone}`]}`}>{tagText}</div>
            <button
                type="button"
                className={`${Styles.outlineBtn} ${Styles.cardBtn} ${accent ? Styles.outlineBtnAccent : ''}`}
                onClick={cta.onClick}
                disabled={cta.disabled}
            >
                {cta.label}
            </button>
        </div>
    );
};


// Карточка прошедшего события
const PastCard = ({event, fallback}) => {
    const {t} = useTranslation();
    return (
        <div className={Styles.pastCard}>
            <div className={Styles.pastImage}>
                <img src={event.image || fallback} alt=""/>
            </div>
            <div className={Styles.pastBody}>
                <div className={Styles.pastTitle}>{event.title}</div>
                <div className={Styles.pastDates}>
                    <DsIcon name="calendarPlain" size={14} className={Styles.pastDatesIcon}/>
                    <span>{formatDate(event.start)} – {formatDate(event.end)}</span>
                </div>
                {event.description && <p className={Styles.pastText}>{event.description}</p>}
                <button
                    type="button"
                    className={`${Styles.outlineBtn} ${Styles.pastBtn}`}
                    onClick={() => openLink(event.resultsLink)}
                    disabled={!event.resultsLink}
                >
                    {t('Результаты')}
                </button>
            </div>
        </div>
    );
};


const Empty = ({text}) => (
    <div className={Styles.empty}>
        <DsIcon name="calendar" size={28} className={Styles.emptyIcon}/>
        <span>{text}</span>
    </div>
);


// Списки событий (макеты 2416:3630 / 2464:4804)
const CabinetEventsTable = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const events = useSelector(store => store.cabinet.events);
    const eventsStarted = useSelector(store => store.cabinet.eventsStarted);
    const joining = useSelector(store => store.cabinet.choiceEventStatusStarted);
    const userData = useSelector(store => store.cabinet.userData);

    const [tab, setTab] = useState('upcoming');
    const [now, setNow] = useState(Date.now());

    // обратный отсчёт обновляется раз в минуту
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    const selectedId = userData?.event_id ?? userData?.user?.event_id ?? userData?.event?.id ?? userData?.user?.event?.id;

    const {upcoming, past, mine} = useMemo(() => {
        const list = extractList(events).map((e, i) => normalize(e, i, now, selectedId));
        const byStart = (a, b) => (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity);
        const upcomingList = list.filter(e => e.status !== 'past').sort((a, b) => {
            if (a.status !== b.status) return a.status === 'live' ? -1 : 1;
            return byStart(a, b);
        });
        const pastList = list.filter(e => e.status === 'past')
            .sort((a, b) => (b.end?.getTime() ?? b.start?.getTime() ?? 0) - (a.end?.getTime() ?? a.start?.getTime() ?? 0));
        return {upcoming: upcomingList, past: pastList, mine: list.filter(e => e.joined)};
    }, [events, now, selectedId]);

    const onJoin = (id) => dispatch(choiceEvent(id, () => navigate('/events')));

    const loading = eventsStarted && !events;
    const emptyUpcoming = loading ? t('Загрузка...') : t('Новые события появятся совсем скоро');
    const emptyPast = loading ? t('Загрузка...') : t('Прошедших событий пока нет');

    const renderUpcoming = (list, emptyText) => list.length
        ? <div className={Styles.cards}>
            {list.map((e, i) => (
                <EventCard key={e.id} event={e} now={now} onJoin={onJoin} joining={joining}
                           fallback={UPCOMING_FALLBACK[i % UPCOMING_FALLBACK.length]}/>
            ))}
        </div>
        : <Empty text={emptyText}/>;

    const renderPast = (list, scroll) => list.length
        ? <div className={`${Styles.pastCards} ${scroll ? Styles.pastScroll : ''}`}>
            {list.map((e, i) => (
                <PastCard key={e.id} event={e} fallback={PAST_FALLBACK[i % PAST_FALLBACK.length]}/>
            ))}
        </div>
        : <Empty text={emptyPast}/>;

    const tabs = [
        {key: 'upcoming', label: t('Предстоящие')},
        {key: 'past', label: t('Прошедшие')},
        {key: 'mine', label: t('Мои события')},
    ];

    return (
        <>
            {/* ===== Desktop ===== */}
            <div className={`${Styles.desktopOnly} ${Styles.desktopWrap}`}>
                <section className={Styles.section}>
                    <div className={Styles.sectionTitle}>{t('Предстоящие события')}</div>
                    {renderUpcoming(upcoming, emptyUpcoming)}
                </section>
                <section className={`${Styles.section} ${Styles.sectionGrow}`}>
                    <div className={Styles.sectionTitle}>{t('Прошедшие события')}</div>
                    {renderPast(past, false)}
                </section>
            </div>

            {/* ===== Mobile ===== */}
            <div className={`${Styles.mobileOnly} ${Styles.mobileWrap}`}>
                <div className={Styles.tabs}>
                    {tabs.map(tb => (
                        <button
                            key={tb.key}
                            type="button"
                            className={`${Styles.tab} ${tab === tb.key ? Styles.tabActive : ''}`}
                            onClick={() => setTab(tb.key)}
                        >
                            {tb.label}
                        </button>
                    ))}
                </div>

                {tab === 'upcoming' && (
                    <>
                        <div className={Styles.mSectionHeader}>
                            <span>{t('Предстоящие события')}</span>
                        </div>
                        {renderUpcoming(upcoming, emptyUpcoming)}
                        <div className={Styles.mSectionHeader}>
                            <span>{t('Прошедшие события')}</span>
                            {past.length > 0 && (
                                <button type="button" className={Styles.mLink} onClick={() => setTab('past')}>
                                    {t('Смотреть все')}
                                    <img src={chevronSmall} alt=""/>
                                </button>
                            )}
                        </div>
                        {renderPast(past, true)}
                    </>
                )}

                {tab === 'past' && renderPast(past, false)}

                {tab === 'mine' && (
                    mine.length
                        ? <div className={Styles.cards}>
                            {mine.map((e, i) => e.status === 'past'
                                ? <PastCard key={e.id} event={e} fallback={PAST_FALLBACK[i % PAST_FALLBACK.length]}/>
                                : <EventCard key={e.id} event={e} now={now} onJoin={onJoin} joining={joining}
                                             fallback={UPCOMING_FALLBACK[i % UPCOMING_FALLBACK.length]}/>
                            )}
                        </div>
                        : <Empty text={loading ? t('Загрузка...') : t('Вы пока не участвуете в событиях')}/>
                )}
            </div>
        </>
    );
};

export default CabinetEventsTable;
